import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Fuel, Loader2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import type {
  EmployeePrompt,
  EmployeePromptFuelAssignment,
  FuelAssignmentConfirmationPrompt,
} from '@shared/types';
import { employeePromptsApi } from '@/api/employee-prompts';
import { fuelApi } from '@/api/fuel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

function promptPath(prompt: EmployeePrompt): string {
  return new URL(prompt.actionUrl, window.location.origin).pathname;
}

function isOnPromptTarget(pathname: string, prompt: EmployeePrompt): boolean {
  const target = promptPath(prompt);
  return pathname === target || pathname.startsWith(`${target}/`);
}

function nextActivePrompt(items: EmployeePrompt[], dismissedKeys: Set<string>, pathname: string): EmployeePrompt | null {
  for (const item of items) {
    if (dismissedKeys.has(item.key)) continue;
    if (isOnPromptTarget(pathname, item)) return null;
    return item;
  }
  return null;
}

function fuelLabel(assignment: EmployeePromptFuelAssignment): string {
  return assignment.isDiesel ? 'Дизель' : 'Бензин';
}

function decimalInput(value: number): string {
  return Number.isFinite(value) ? String(value).replace('.', ',') : '';
}

function parseDecimalInput(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function EmployeePromptHost() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState<EmployeePrompt[]>([]);
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(() => new Set());
  const [refreshToken, setRefreshToken] = useState(0);
  const [confirmingFuel, setConfirmingFuel] = useState(false);
  const [fuelCar, setFuelCar] = useState('');
  const [fuelLicense, setFuelLicense] = useState('');
  const [fuelConsumption, setFuelConsumption] = useState('');

  const refreshPrompts = useCallback((options: { resetDismissed?: boolean } = {}) => {
    if (options.resetDismissed !== false) {
      setDismissedKeys(new Set());
    }
    setRefreshToken((value) => value + 1);
  }, []);

  useEffect(() => {
    refreshPrompts();
  }, [location.pathname, location.search, refreshPrompts]);

  useEffect(() => {
    const handleFocus = () => refreshPrompts();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshPrompts();
    };
    const handlePageShow = () => refreshPrompts();

    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshPrompts]);

  useEffect(() => {
    if (refreshToken === 0) return;
    let cancelled = false;
    employeePromptsApi.listMine('mobile')
      .then((response) => {
        if (cancelled) return;
        // Prompts are an optional cross-cutting UX aid. A stale proxy response,
        // interrupted token renewal, or temporarily mismatched API deployment
        // must not crash every authenticated mobile route. Keep the last
        // validated list and let the next focus/navigation refresh retry.
        if (!Array.isArray(response?.items)) return;
        setItems(response.items);
      })
      .catch((error) => {
        if (import.meta.env.DEV) {
          console.error('Employee prompts failed:', error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const activePrompt = useMemo(
    () => nextActivePrompt(items, dismissedKeys, location.pathname),
    [dismissedKeys, items, location.pathname],
  );

  const fuelPrompt = activePrompt?.type === 'fuel.assignment_confirmation_required'
    ? activePrompt as FuelAssignmentConfirmationPrompt
    : null;

  useEffect(() => {
    if (!fuelPrompt) return;
    const assignment = fuelPrompt.payload.assignment;
    setFuelCar(assignment.car);
    setFuelLicense(assignment.license);
    setFuelConsumption(decimalInput(assignment.consumptionPer100Km));
  }, [fuelPrompt?.key, fuelPrompt]);

  const dismissActive = useCallback(() => {
    if (!activePrompt) return;
    setDismissedKeys((current) => new Set(current).add(activePrompt.key));
  }, [activePrompt]);

  const openActiveTarget = useCallback(() => {
    if (!activePrompt) return;
    dismissActive();
    navigate(activePrompt.actionUrl);
  }, [activePrompt, dismissActive, navigate]);

  const confirmFuel = useCallback(async () => {
    if (!fuelPrompt) return;
    const consumptionPer100Km = parseDecimalInput(fuelConsumption);
    if (!fuelCar.trim() || !fuelLicense.trim() || !consumptionPer100Km) return;
    setConfirmingFuel(true);
    try {
      await fuelApi.confirmSelfAssignment({
        month: fuelPrompt.period,
        car: fuelCar.trim(),
        license: fuelLicense.trim(),
        consumptionPer100Km,
      });
      toast.success('Машина и карта подтверждены');
      dismissActive();
      refreshPrompts({ resetDismissed: false });
    } catch {
      toast.error('Не удалось подтвердить машину и карту');
    } finally {
      setConfirmingFuel(false);
    }
  }, [dismissActive, fuelCar, fuelConsumption, fuelLicense, fuelPrompt, refreshPrompts]);

  const fuelConsumptionValue = parseDecimalInput(fuelConsumption);
  const canConfirmFuel = Boolean(
    fuelPrompt &&
    fuelCar.trim() &&
    fuelLicense.trim() &&
    fuelConsumptionValue &&
    !confirmingFuel,
  );

  return (
    <Dialog
      open={Boolean(activePrompt)}
      onOpenChange={(open) => {
        if (!open && !confirmingFuel) dismissActive();
      }}
    >
      <DialogContent className="max-w-[calc(100vw-2rem)]">
        {activePrompt?.type === 'personal_data.monthly_review_required' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                {activePrompt.title}
              </DialogTitle>
              <DialogDescription>{activePrompt.message}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={dismissActive}>Позже</Button>
              <Button onClick={openActiveTarget}>
                <ArrowRight className="h-4 w-4" />
                Открыть сверку
              </Button>
            </DialogFooter>
          </>
        ) : null}

        {fuelPrompt ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Fuel className="h-5 w-5" />
                {fuelPrompt.title}
              </DialogTitle>
              <DialogDescription>{fuelPrompt.message}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-muted-foreground">Карта</span>
                  <span className="font-medium">{fuelPrompt.payload.assignment.chip}</span>
                  <span className="text-muted-foreground">Топливо</span>
                  <span className="font-medium">{fuelLabel(fuelPrompt.payload.assignment)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="mobile-employee-prompt-fuel-car">Машина</label>
                <Input
                  id="mobile-employee-prompt-fuel-car"
                  value={fuelCar}
                  onChange={(event) => setFuelCar(event.target.value)}
                  placeholder="Марка и номер"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="mobile-employee-prompt-fuel-license">Права</label>
                <Input
                  id="mobile-employee-prompt-fuel-license"
                  value={fuelLicense}
                  onChange={(event) => setFuelLicense(event.target.value)}
                  placeholder="Номер водительского удостоверения"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="mobile-employee-prompt-fuel-consumption">Расход, л/100 км</label>
                <Input
                  id="mobile-employee-prompt-fuel-consumption"
                  inputMode="decimal"
                  value={fuelConsumption}
                  onChange={(event) => setFuelConsumption(event.target.value)}
                  placeholder="7,5"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={dismissActive} disabled={confirmingFuel}>Позже</Button>
              <Button variant="secondary" onClick={openActiveTarget} disabled={confirmingFuel}>
                <ArrowRight className="h-4 w-4" />
                Раздел
              </Button>
              <Button onClick={confirmFuel} disabled={!canConfirmFuel}>
                {confirmingFuel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Подтвердить
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
